/**
 * Hero — landing first screen.
 *
 * Two columns at md+; stacked on small.
 *  Left  : tagline · headline · subtitle · 2 CTAs · note
 *  Right : animated terminal preview (typewrites a few mock log lines)
 *
 * The terminal is mock-only — it shows the *vibe* of automated trading
 * (events scroll, no manual input) without revealing any actual logic.
 * The lines come from i18n so they translate per language.
 */
import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/ui/Button'
import { useAuthStore } from '@/stores/auth'

export function Hero() {
  const { t } = useTranslation('landing')
  const isAuthed = useAuthStore((s) => !!s.accessToken)

  return (
    <section className="px-6 pt-16 pb-20 md:pt-24 md:pb-28 relative overflow-hidden">
      {/* Quiet ambient glow — bleeds fire to give the dark surface depth */}
      <div
        aria-hidden
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            'radial-gradient(800px 400px at 50% -10%, rgba(255,69,36,0.12), transparent 60%)',
        }}
      />

      <div className="relative max-w-6xl mx-auto grid md:grid-cols-12 gap-10 items-center">
        {/* Left column — copy */}
        <div className="md:col-span-7">
          <div className="text-xs uppercase tracking-[0.2em] text-fire mb-4">
            {t('tagline')}
          </div>
          <h1 className="text-4xl sm:text-5xl md:text-6xl font-semibold leading-[1.05] tracking-tight">
            {t('hero.title_line_1')}<br />
            {t('hero.title_line_2')}
          </h1>
          <p className="mt-5 text-base md:text-lg text-textDim max-w-xl">
            {t('hero.subtitle')}
          </p>

          <div className="mt-8 flex flex-wrap items-center gap-3">
            {isAuthed ? (
              <Link to="/app"><Button size="lg">{t('cta.primary')}</Button></Link>
            ) : (
              <>
                <Link to="/register"><Button size="lg">{t('cta.primary')}</Button></Link>
                <Link to="/login">
                  <Button size="lg" variant="secondary">{t('cta.secondary')}</Button>
                </Link>
              </>
            )}
          </div>
          <p className="mt-5 text-xs text-textFaint font-mono">
            {t('cta.note')}
          </p>
        </div>

        {/* Right column — terminal preview */}
        <div className="md:col-span-5">
          <TerminalPreview />
        </div>
      </div>
    </section>
  )
}

// ─── Mock terminal ───────────────────────────────────────────────────

function TerminalPreview() {
  const { t } = useTranslation('landing')
  const lines = (t('hero.terminal.lines', { returnObjects: true }) as string[]) ?? []
  const [visibleCount, setVisibleCount] = useState(0)

  // Reveal the lines one by one, then loop after a pause. Pure visual —
  // never advances faster than 600ms so it doesn't feel frantic.
  useEffect(() => {
    if (!lines.length) return
    let i = 0
    const id = setInterval(() => {
      i = (i + 1) % (lines.length + 4)            // brief pause at the end
      setVisibleCount(Math.min(i, lines.length))
    }, 600)
    return () => clearInterval(id)
  }, [lines.length])

  return (
    <div className="bg-surface border border-line rounded shadow-soft overflow-hidden">
      {/* Title bar */}
      <div className="flex items-center px-3 py-2 border-b border-line text-xs text-textFaint font-mono">
        <span className="dot mr-1.5" style={{ background: 'var(--rose)' }} />
        <span className="dot mr-1.5" style={{ background: 'var(--amber)' }} />
        <span className="dot mr-3" style={{ background: 'var(--pulse)' }} />
        <span className="ml-1">{t('hero.terminal.header')}</span>
        <span className="ml-auto flex items-center gap-1.5">
          <span className="dot animate-blink" style={{ background: 'var(--pulse)' }} />
          <span>connected</span>
        </span>
      </div>

      {/* Log body */}
      <div className="font-mono text-xs leading-relaxed p-4 min-h-[200px]">
        {lines.slice(0, visibleCount).map((raw, idx) => (
          <TerminalLine key={idx} raw={raw} dim={idx < visibleCount - 1} />
        ))}
        {visibleCount < lines.length && (
          <span className="inline-block w-1.5 h-3 bg-textDim align-middle animate-blink" />
        )}
      </div>
    </div>
  )
}

function TerminalLine({ raw, dim }: { raw: string; dim: boolean }) {
  // Detect kind for colouring — "enter" green, "exit" rose/pulse based
  // on % sign, "watch" sky, "signal" neutral. Pure presentational.
  let cls = 'text-textDim'
  if (raw.includes('enter')) cls = 'text-pulse'
  else if (raw.includes('exit')) cls = raw.includes('-') ? 'text-rose' : 'text-pulse'
  else if (raw.includes('watch') || raw.includes('смотрим')) cls = 'text-sky'
  else if (raw.includes('вход')) cls = 'text-pulse'
  else if (raw.includes('выход')) cls = raw.includes('-') ? 'text-rose' : 'text-pulse'
  return (
    <div className={`${cls} ${dim ? 'opacity-70' : 'opacity-100'}`}>
      <span className="text-textFaint mr-2">›</span>
      <span className="whitespace-pre">{raw}</span>
    </div>
  )
}
